import { z } from 'zod';
import type { CountryCode } from './country.js';
import type { ChatMessageDeletedByRole, ChatMessageType } from './chat.js';

/**
 * F43 — Admin User Management & Moderation frozen API contracts (the central
 * report queue + scoped chat-history moderation viewer + reporter anonymity).
 *
 * The central queue is a polymorphic `user_reports` table (8 subject types,
 * 6 reasons). A consumer files via `POST /me/reports` (any of the 8 subjects);
 * admins triage via `GET /admin/reports` (which UNIONs `user_reports` with F32's
 * legacy `open_game_reports` at read time). Each report deep-links into the
 * owning feature's admin surface (§2.5); F43 owns the action only for `user` +
 * `chat_message` subjects.
 *
 * Reporter anonymity (D10): `reporterUserId` is ALWAYS stored + visible to
 * admins (accountability + the one-open-per-reporter guard), and NEVER surfaced
 * to the reported party. The admin-only `ReportDetail` includes it; the
 * reported-user-facing `ReportPublic` omits it.
 *
 * Cross-bundler note: imports use `.js` extensions per the F03 cross-bundler
 * seam (consumed by both NodeNext API and Bundler/Metro web+mobile chains).
 *
 * See plans/43_F43_admin_user_management.md §2.5/§2.7/§2.9/§4.
 */

// ─── Constants ──────────────────────────────────────────────────────────────

export const REPORT_FREE_TEXT_MAX = 1000;
export const REPORT_ACTION_TAKEN_MAX = 1000;
export const REPORT_RESOLUTION_REASON_MAX = 1000;

/** Scoped chat-context window defaults + bound (D4: ±20). */
export const CHAT_CONTEXT_DEFAULT_WINDOW = 20;
export const CHAT_CONTEXT_MAX_WINDOW = 50;

// ─── Enums ──────────────────────────────────────────────────────────────────

/**
 * The 8 polymorphic report subject types (D7). `subjectId` is a polymorphic
 * uuid with NO FK. F43 owns the action for `user` + `chat_message`; the rest
 * deep-link into the owning feature's admin surface (§2.5).
 */
export const REPORT_SUBJECT_TYPES = [
  'user',
  'chat_message',
  'review',
  'badge',
  'placement',
  'promo',
  'tournament',
  'tournament_announcement',
] as const;
export type ReportSubjectType = (typeof REPORT_SUBJECT_TYPES)[number];
export const ReportSubjectTypeSchema = z.enum(REPORT_SUBJECT_TYPES);

/** The 6 report reasons (the brief's `reason_category`). */
export const REPORT_REASONS = [
  'harassment',
  'spam',
  'inappropriate_content',
  'fraud',
  'impersonation',
  'other',
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const ReportReasonSchema = z.enum(REPORT_REASONS);

/** Report lifecycle status. */
export const REPORT_STATUSES = ['open', 'under_review', 'actioned', 'dismissed'] as const;
export type ReportStatus = (typeof REPORT_STATUSES)[number];
export const ReportStatusSchema = z.enum(REPORT_STATUSES);

// ─── Error codes (closed set — no other members) ─────────────────────────────

export const MODERATION_ERROR_CODES = [
  'REPORT_ALREADY_FILED',
  'REPORT_NOT_FOUND',
  'CHAT_CONTEXT_NOT_A_MESSAGE_REPORT',
  'STALE_USER',
  'USER_SUSPENDED',
  'BULK_LIMIT_EXCEEDED',
  // FIX 5.23 (§12.9-3) — reassign-reports without an assignee.
  'BULK_ASSIGNEE_REQUIRED',
  'GDPR_REQUEST_ALREADY_OPEN',
  // FIX 5.23 — an unknown GDPR request / one no longer open for processing.
  'GDPR_REQUEST_NOT_FOUND',
  'GDPR_REQUEST_NOT_PROCESSABLE',
  // An unknown tournament announcement on the admin hide path (it said
  // REPORT_NOT_FOUND).
  'ANNOUNCEMENT_NOT_FOUND',
  'EVIDENCE_UPLOAD_FAILED',
  // The report carries no evidence object (or it is no longer retrievable) —
  // returned by GET /admin/reports/:id/evidence-download-url.
  'EVIDENCE_NOT_FOUND',
] as const;
export type ModerationErrorCode = (typeof MODERATION_ERROR_CODES)[number];

// ─── Input schemas ────────────────────────────────────────────────────────────

/**
 * POST /me/reports — a consumer files a report against any of the 8 subjects.
 * `evidenceUrl` is the S3 key returned by the presigned-PUT flow (optional).
 * A second open/under_review report by the same reporter on the same subject →
 * `REPORT_ALREADY_FILED` (the partial-unique guard).
 */
export const FileReportInputSchema = z
  .object({
    subjectType: ReportSubjectTypeSchema,
    subjectId: z.string().uuid(),
    reason: ReportReasonSchema,
    freeText: z.string().trim().min(1).max(REPORT_FREE_TEXT_MAX).optional(),
    evidenceUrl: z.string().trim().min(1).optional(),
  })
  .strict();
export type FileReportInput = z.infer<typeof FileReportInputSchema>;

/**
 * POST /admin/reports/:id/action — mandatory `actionTaken`; sets status
 * 'actioned' + dispatches `report_actioned` to the reporter (masked).
 */
export const ResolveReportInputSchema = z
  .object({
    actionTaken: z.string().trim().min(1).max(REPORT_ACTION_TAKEN_MAX),
    resolutionReason: z.string().trim().min(1).max(REPORT_RESOLUTION_REASON_MAX).optional(),
  })
  .strict();
export type ResolveReportInput = z.infer<typeof ResolveReportInputSchema>;

/**
 * POST /admin/reports/:id/dismiss — mandatory reason; sets status 'dismissed' +
 * dispatches `report_dismissed` (brief reason + contact-support appeal path, D11).
 */
export const DismissReportInputSchema = z
  .object({
    resolutionReason: z.string().trim().min(1).max(REPORT_RESOLUTION_REASON_MAX),
  })
  .strict();
export type DismissReportInput = z.infer<typeof DismissReportInputSchema>;

/** POST /admin/reports/:id/reassign — set `assigned_admin_id`. */
export const ReassignReportInputSchema = z
  .object({
    assignedAdminId: z.string().uuid(),
  })
  .strict();
export type ReassignReportInput = z.infer<typeof ReassignReportInputSchema>;

/**
 * GET /admin/reports/:reportId/chat-context — the ±N window around a reported
 * chat_message (§2.7). `before`/`after` default to 20, capped at 50.
 */
export const ChatContextQuerySchema = z
  .object({
    before: z.coerce
      .number()
      .int()
      .min(0)
      .max(CHAT_CONTEXT_MAX_WINDOW)
      .default(CHAT_CONTEXT_DEFAULT_WINDOW),
    after: z.coerce
      .number()
      .int()
      .min(0)
      .max(CHAT_CONTEXT_MAX_WINDOW)
      .default(CHAT_CONTEXT_DEFAULT_WINDOW),
  })
  .strict();
export type ChatContextQuery = z.infer<typeof ChatContextQuerySchema>;

// ─── Read shapes ──────────────────────────────────────────────────────────────

/**
 * A row in the central admin queue (read-side). For native `user_reports` rows
 * `subjectType` is one of the 8 enum values; for UNION'd legacy F32 open-game
 * reports the read-mapper attaches the synthetic `subjectType:'open_game'`
 * (intentionally NOT a `user_reports.subject_type` CHECK value — §2.5).
 */
export interface ReportListItem {
  readonly id: string;
  /** 8 native subject types ∪ the synthetic 'open_game' for UNION'd legacy rows. */
  readonly subjectType: ReportSubjectType | 'open_game';
  readonly subjectId: string;
  readonly reason: ReportReason;
  readonly status: ReportStatus;
  readonly assignedAdminId: string | null;
  /** Always present in admin shapes (D10 — visible to admins, never to the reported party). */
  readonly reporterUserId: string;
  readonly createdAt: string;
  /** True for rows sourced from the legacy F32 `open_game_reports` table. */
  readonly isLegacyOpenGame: boolean;
}

/**
 * Full admin report detail. Includes `reporterUserId` (D10 — admin shape only).
 * `deepLinkPath` is the owning feature's admin surface for the action (§2.5).
 */
export interface ReportDetail {
  readonly id: string;
  readonly subjectType: ReportSubjectType | 'open_game';
  readonly subjectId: string;
  readonly reporterUserId: string;
  readonly reason: ReportReason;
  readonly freeText: string | null;
  readonly evidenceUrl: string | null;
  readonly status: ReportStatus;
  readonly assignedAdminId: string | null;
  readonly actionTaken: string | null;
  readonly resolutionReason: string | null;
  readonly resolvedByAdminId: string | null;
  readonly resolvedAt: string | null;
  /** The owning feature's admin surface for the action (§2.5). */
  readonly deepLinkPath: string | null;
  readonly countryCode: CountryCode;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly isLegacyOpenGame: boolean;
}

/**
 * The reporter-facing / reported-user-facing report shape. Deliberately OMITS
 * `reporterUserId` (D9/D10 — the reported party is never told who reported them).
 */
export interface ReportPublic {
  readonly id: string;
  readonly subjectType: ReportSubjectType;
  readonly subjectId: string;
  readonly reason: ReportReason;
  readonly status: ReportStatus;
  readonly createdAt: string;
}

/** One message in the scoped chat-context viewer (§2.7). Soft-deleted rows are SHOWN. */
export interface ChatContextMessage {
  readonly id: string;
  readonly roomId: string;
  readonly senderUserId: string | null;
  readonly senderDisplayName: string | null;
  readonly type: ChatMessageType;
  readonly body: string | null;
  readonly createdAt: string;
  /** True when this is the reported (anchor) message. */
  readonly isAnchor: boolean;
  readonly deletedAt: string | null;
  /** Who soft-deleted the message ('admin' after a moderation soft-delete). */
  readonly deletedByRole: ChatMessageDeletedByRole | null;
}

/** The ±N window returned by the chat-context viewer; one read writes one access-log row. */
export interface ChatContextWindow {
  readonly reportId: string;
  readonly roomId: string;
  readonly anchorMessageId: string;
  readonly windowBefore: number;
  readonly windowAfter: number;
  readonly messages: readonly ChatContextMessage[];
}

// ─── Cross-feature deep-link contract (§2.5) ─────────────────────────────────

/**
 * One deep-link contract entry for a report subject (§2.5). The central report
 * queue uses this to build the owning feature's admin surface link + to know
 * which admin permission gates the action there + what actions are available.
 *
 * The `adminPath` is an `apps/admin` React-Router path template with a single
 * `:id` placeholder substituted by the report's `subjectId` (F43 owns the
 * action for `user` + `chat_message`; the rest deep-link into the owning
 * feature's existing admin surface — F37/F39/F40/F41/F32).
 *
 * The `permission` is the admin permission required to ACT on that surface
 * (NOT to read the central queue — that's always `reports:read`). For the
 * subjects F43 deep-links into, this is the owning feature's permission; for
 * `user` + `chat_message` it's F43's own.
 */
export interface ReportSubjectDeepLink {
  /** `apps/admin` route template with a `:id` slot (substitute `subjectId`). */
  readonly adminPath: string;
  /** Admin permission required to ACT on the owning surface (string — owning features' perms aren't in @bravemove/types' `Permission` union universe of F43). */
  readonly permission: string;
  /** The moderation actions available on the owning surface (display only). */
  readonly actions: readonly string[];
  /**
   * When true, the `:id` slot is NOT the report's `subjectId` — it is a
   * server-resolved PARENT/OWNER id (the badge's owning user; the announcement's
   * parent tournament). `buildReportDeepLinkPath` returns `null` for these
   * subjects unless the resolved id is supplied, so the UI never renders a
   * dead link with the wrong entity id substituted.
   */
  readonly requiresResolvedId?: boolean;
}

/**
 * The frozen deep-link contract map (§2.5). Keyed by the 8 native subject types
 * PLUS the synthetic `'open_game'` for UNION'd legacy F32 rows. The admin UI +
 * the API read-mapper consume this verbatim. `:id` in `adminPath` is the
 * report's `subjectId`.
 *
 * F43 owns the action for `user` (suspend/restore/note on /admin/users/:id) and
 * `chat_message` (view ±N window + soft-delete on /admin/reports/:id). The rest
 * deep-link into the owning feature's admin surface.
 */
export const REPORT_SUBJECT_DEEPLINK: Readonly<
  Record<ReportSubjectType | 'open_game', ReportSubjectDeepLink>
> = {
  user: {
    adminPath: '/users/:id',
    permission: 'users:suspend',
    actions: ['suspend', 'restore', 'note'],
  },
  chat_message: {
    // F43 owns this — the chat-context viewer lives on the report detail page.
    adminPath: '/reports/:id',
    permission: 'chat:moderate',
    actions: ['view_window', 'soft_delete'],
  },
  review: {
    // F37's review-moderation surface (`/reviews/:id` was never a real route).
    adminPath: '/review-moderation/:id',
    permission: 'reviews:moderate',
    actions: ['hide', 'remove', 'restore', 'edit', 'lock'],
  },
  badge: {
    // The badge's moderation surface is its OWNER's user detail (reviews-badges
    // tab carries the revoke/restore dialog). `:id` = the badge owner's user id,
    // resolved server-side from `user_badges.user_id` (NOT the badge subjectId).
    adminPath: '/users/:id',
    permission: 'endorsements:moderate',
    actions: ['remove', 'restore'],
    requiresResolvedId: true,
  },
  placement: {
    // The per-placement admin detail lives on the approvals surface
    // (`/placements/:id` was never a real route).
    adminPath: '/placement-approvals/:id',
    permission: 'placement_approvals:approve',
    actions: ['approve', 'reject', 'cancel'],
  },
  promo: {
    adminPath: '/promotions/:id',
    permission: 'promotions:moderate',
    actions: ['suspend', 'restore'],
  },
  tournament: {
    adminPath: '/tournaments/:id',
    permission: 'tournaments:moderate',
    actions: ['suspend', 'unsuspend', 'cancel'],
  },
  tournament_announcement: {
    // F43-new hide route — the only subject lacking any prior moderation path.
    // `:id` = the PARENT tournament id, resolved server-side from
    // `tournament_announcements.tournament_id` (substituting the announcement
    // id produced a guaranteed 404 on the tournament detail page).
    adminPath: '/tournaments/:id',
    permission: 'tournaments:moderate',
    actions: ['hide'],
    requiresResolvedId: true,
  },
  open_game: {
    // Synthetic union subject (legacy F32) — resolution still happens in F32's
    // own admin surface; F43's central queue is read-only over it.
    adminPath: '/open-games/:id',
    permission: 'open_games:moderate',
    actions: ['suspend', 'unsuspend', 'resolve'],
  },
};

/**
 * Builds the concrete admin deep-link path for a report by substituting the
 * `subjectId` into the `:id` slot of the subject's `adminPath` template.
 * Returns `null` for an unknown subject type (defensive).
 *
 * Subjects flagged `requiresResolvedId` (badge → owning user; tournament
 * announcement → parent tournament) substitute the server-`resolvedId` instead;
 * without it the link resolves to `null` so the UI hides the deep-link button
 * rather than navigating to a guaranteed wrong/404 target.
 */
export function buildReportDeepLinkPath(
  subjectType: ReportSubjectType | 'open_game',
  subjectId: string,
  resolvedId?: string | null,
): string | null {
  const entry = REPORT_SUBJECT_DEEPLINK[subjectType];
  if (!entry) return null;
  if (entry.requiresResolvedId) {
    return resolvedId ? entry.adminPath.replace(':id', resolvedId) : null;
  }
  return entry.adminPath.replace(':id', subjectId);
}

// ─── Legacy F32 open-game-report enum normalization (read-side only, §2.5) ────

/**
 * The legacy F32 `open_game_reports` table uses a DIFFERENT vocabulary than
 * `user_reports`. The central-queue read-mapper normalizes — reason
 * `'inappropriate'→'inappropriate_content'`, `'misleading'→'other'`
 * (`'spam'`/`'other'` pass through). This is a pure read-side mapping; F32's
 * schema is untouched (D7).
 */
export function normalizeOpenGameReportReason(
  reason: 'spam' | 'inappropriate' | 'misleading' | 'other',
): ReportReason {
  switch (reason) {
    case 'inappropriate':
      return 'inappropriate_content';
    case 'misleading':
      return 'other';
    case 'spam':
      return 'spam';
    case 'other':
      return 'other';
  }
}

/**
 * Normalizes the legacy F32 `open_game_reports.status` vocabulary to the central
 * `ReportStatus` — `'reviewed'→'actioned'` (`'open'`/`'dismissed'` pass through).
 */
export function normalizeOpenGameReportStatus(
  status: 'open' | 'reviewed' | 'dismissed',
): ReportStatus {
  switch (status) {
    case 'reviewed':
      return 'actioned';
    case 'open':
      return 'open';
    case 'dismissed':
      return 'dismissed';
  }
}
